import { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { formatInstant } from '@/lib/formatInstant';
import { useDisplayTimezone } from '@/lib/useDisplayTimezone';
import type { AuditEvent } from '@/types/api';

interface OverrideHistoryProps {
  entityType: string;
  entityId: string;
}

export function OverrideHistory({ entityType, entityId }: OverrideHistoryProps) {
  const tz = useDisplayTimezone();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expanded || !entityId) return;
    setLoading(true);
    api.audit
      .events(entityType, entityId)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [entityType, entityId, expanded]);

  if (!entityId) return null;

  return (
    <div className="mt-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
      >
        <Clock className="w-3 h-3" />
        <span>Override history</span>
        {expanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
      </button>

      {expanded && (
        <div className="mt-2 ml-4 border-l-2 border-slate-100 pl-3 space-y-2" onClick={(e) => e.stopPropagation()}>
          {loading && (
            <p className="text-xs text-slate-400">Loading...</p>
          )}
          {!loading && events.length === 0 && (
            <p className="text-xs text-slate-400">No override history</p>
          )}
          {events.map((event) => (
            <div key={event.id} className="text-xs">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${
                    event.eventType.includes('restored')
                      ? 'bg-green-400'
                      : 'bg-orange-400'
                  }`}
                />
                <span className="font-medium text-slate-600">
                  {event.eventType.includes('restored') ? 'Restored' : 'Dismissed'}
                </span>
                <span className="text-slate-400">
                  {formatInstant(event.timestampUtc, tz)}
                </span>
              </div>
              {event.rationale && (
                <p className="ml-3.5 text-slate-500 mt-0.5">
                  {event.rationale}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
